export type PlatformKey = 'quickbooks' | 'shopify' | 'csv';

export interface HolisticStats {
  totalTransactions: number;
  totalTaxCalculated: number;
  totalPendingTax: number;
  totalAmount: number;
  totalTaxAmount: number;
  platformBreakdown: Record<
    PlatformKey,
    {
      transactions: number;
      taxCalculated: number;
      pendingTax: number;
      failedTax: number;
      amount: number;
      taxAmount: number;
    }
  >;
}

const PLATFORM_KEYS: PlatformKey[] = ['quickbooks', 'shopify', 'csv'];

const normalizePlatform = (name?: string | null): PlatformKey | null => {
  if (!name) return null;
  const lower = name.toLowerCase();
  if (lower.includes('quickbooks')) return 'quickbooks';
  if (lower.includes('shopify')) return 'shopify';
  if (lower.includes('csv')) return 'csv';
  return null;
};

export const buildInitialHolisticStats = (): HolisticStats => ({
  totalTransactions: 0,
  totalTaxCalculated: 0,
  totalPendingTax: 0,
  totalAmount: 0,
  totalTaxAmount: 0,
  platformBreakdown: {
    quickbooks: { transactions: 0, taxCalculated: 0, pendingTax: 0, failedTax: 0, amount: 0, taxAmount: 0 },
    shopify: { transactions: 0, taxCalculated: 0, pendingTax: 0, failedTax: 0, amount: 0, taxAmount: 0 },
    csv: { transactions: 0, taxCalculated: 0, pendingTax: 0, failedTax: 0, amount: 0, taxAmount: 0 },
  },
});

export function buildHolisticStats(
  integrations: any[],
  companyId?: number | null,
): HolisticStats {
  const stats = buildInitialHolisticStats();

  integrations.forEach((integration) => {
    if (companyId && companyId !== 0 && integration.company_id && integration.company_id !== companyId) {
      return;
    }
    const platformName = integration.platform?.name || integration.platform_name;
    const platformKey = normalizePlatform(platformName);
    if (!platformKey) {
      return;
    }
    const breakdown = stats.platformBreakdown[platformKey];
    const txStats = integration.transaction_stats || {};
    breakdown.transactions += Number(txStats.total_transactions || 0);
    breakdown.taxCalculated += Number(txStats.tax_calculated || 0);
    breakdown.pendingTax += Number(txStats.pending_tax_calculation || 0);
    const failedFromStatus = txStats.by_status?.TAX_CALCULATION_FAILED ?? 0;
    breakdown.failedTax += Number(failedFromStatus || 0);
    breakdown.amount += Number(txStats.amount_sum || 0);
    breakdown.taxAmount += Number(txStats.tax_sum || 0);
  });

  stats.totalTransactions = PLATFORM_KEYS.reduce(
    (sum, key) => sum + stats.platformBreakdown[key].transactions,
    0,
  );
  stats.totalTaxCalculated = PLATFORM_KEYS.reduce(
    (sum, key) => sum + stats.platformBreakdown[key].taxCalculated,
    0,
  );
  stats.totalPendingTax = PLATFORM_KEYS.reduce(
    (sum, key) => sum + stats.platformBreakdown[key].pendingTax,
    0,
  );
  stats.totalAmount = PLATFORM_KEYS.reduce(
    (sum, key) => sum + stats.platformBreakdown[key].amount,
    0,
  );
  stats.totalTaxAmount = PLATFORM_KEYS.reduce(
    (sum, key) => sum + stats.platformBreakdown[key].taxAmount,
    0,
  );

  return stats;
}
